/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UiInlineSelectPill from 'components/Ui/UiInlineSelectPill'
import UiOfferStatusPill from 'components/Ui/UiOfferStatusPill'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import EditOfferStatusModal from 'components/Offers/EditOfferStatusModal'
import EditLeadStatusModal from 'components/leads/EditLeadStatusModal'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { useAppDispatch } from 'helpers/store'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import LeadsAction from 'store/leads/LeadsAction'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import { OffersAction } from 'store/offers/OffersAction'
import { pushOffer } from 'store/offers/OffersReducer'
import { Lead } from 'types/entities/Lead'
import { Offer } from 'types/entities/Offer'
import { LeadCategory } from 'types/enums/LeadCategory'
import { ModalSize } from 'types/enums/ModalSize'
import { OfferStatus } from 'types/enums/OfferStatus'
import { toast } from 'utils/toastUtils'
import {
    getOfferDeletableReservationId,
    getOfferEditLockedTooltip,
    useOfferStatusOptions,
} from 'utils/offers/offersUtils'
import {
    getLinkedOpportunityRejectionStatus,
    isLinkedOpportunityLead,
} from 'utils/leads/linkedOpportunityRejectionUtils'

interface UiInlineOfferStatusSelectProps {
    offer: Offer
}

const fetchLinkedLead = async (
    dispatch: ReturnType<typeof useAppDispatch>,
    offer: Offer
): Promise<Lead | null> => {
    const leadId = offer.lead_id ?? offer.lead?.id
    if (!leadId) {
        return null
    }

    const categoryHint = offer.lead?.category
    const fetchAction =
        categoryHint === LeadCategory.Opportunity
            ? OpportunitiesAction.fetchLeadById({ id: leadId } as Lead)
            : LeadsAction.fetchLeadById({ id: leadId } as Lead)

    try {
        const lead = await dispatch(fetchAction)
        return isLinkedOpportunityLead(lead) ? lead : null
    } catch {
        return null
    }
}

const UiInlineOfferStatusSelect: React.FC<UiInlineOfferStatusSelectProps> = ({ offer }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { options } = useOfferStatusOptions()
    const [currentStatus, setCurrentStatus] = useState(offer.status)

    useEffect(() => setCurrentStatus(offer.status), [offer.status])

    const refreshOffer = async () => {
        const updated = await dispatch(OffersAction.fetchOfferByIdRaw(offer.id))
        if (updated) {
            dispatch(pushOffer(updated))
            if (updated.status) {
                setCurrentStatus(updated.status)
            }
        }
    }

    const openOfferRejectionModal = () => {
        dispatch(
            openSecondaryModal(
                <EditOfferStatusModal
                    offer={offer}
                    initialStatus={OfferStatus.Rejected}
                    onSuccess={() => {
                        void refreshOffer()
                    }}
                    isSecondary
                />,
                { modalSize: ModalSize.Small },
                offer.status === OfferStatus.Rejected ? t('Rejection Details') : t('Edit Offer Status')
            )
        )
    }

    const openOpportunityRejectionModal = (linkedOpportunity: Lead) => {
        dispatch(
            openSecondaryModal(
                <EditLeadStatusModal
                    lead={linkedOpportunity}
                    rejectionStatus={getLinkedOpportunityRejectionStatus(linkedOpportunity)}
                    onSuccess={() => {
                        void refreshOffer()
                    }}
                    isSecondary
                />,
                { modalSize: ModalSize.Small },
                t('Rejection Details')
            )
        )
    }

    const openRejectionFlow = async () => {
        const linkedOpportunity = await fetchLinkedLead(dispatch, offer)
        if (linkedOpportunity) {
            openOpportunityRejectionModal(linkedOpportunity)
            return
        }
        openOfferRejectionModal()
    }

    const applyStatus = async (newStatus: OfferStatus) => {
        await dispatch(
            OffersAction.updateOffer({
                id: offer.id,
                seller_id: offer.seller_id,
                status: newStatus,
            })
        )
        setCurrentStatus(newStatus)
        toast.success(t('Offer status updated'))
    }

    const handleChange = async (newStatus: OfferStatus) => {
        if (currentStatus === OfferStatus.Rejected) {
            return
        }

        if (newStatus === currentStatus) {
            return
        }

        if (newStatus === OfferStatus.Accepted && currentStatus !== OfferStatus.Accepted) {
            toast.warning(
                t('Accepted status can only be set by creating a booking from an offer.')
            )
            return
        }

        const linkedReservationId = getOfferDeletableReservationId(offer)

        if (
            newStatus === OfferStatus.Reconfirm &&
            currentStatus !== OfferStatus.Reconfirm &&
            !!linkedReservationId
        ) {
            dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Reconfirm Offer')}
                        message={t(
                            'This offer already has a booking. Continuing will cancel the existing booking. After finishing your changes, click Create Booking again. A back-office change note will be created for admin@arbopark.ch and Lea Petersen.'
                        )}
                        confirmLabel={t('Continue') as string}
                        cancelLabel={t('Cancel') as string}
                        onConfirm={async () => {
                            dispatch(closeSecondaryModal())
                            try {
                                await dispatch(
                                    BookingLoginAction.fetchDeleteReservationOffer(linkedReservationId)
                                )
                                dispatch(OffersAction.clearSelectedOfferReservation())
                                await applyStatus(OfferStatus.Reconfirm)
                                await dispatch(
                                    OffersAction.fetchAddOfferNote(
                                        offer.id,
                                        `RECONFIRM: Existing booking #${linkedReservationId} was cancelled before offer changes. Please inform admin@arbopark.ch and Lea Petersen. After offer updates, create a new booking from target calculation.`
                                    )
                                )
                                await refreshOffer()
                                toast.success(
                                    t(
                                        'Booking was cancelled and offer status was changed to Reconfirm. After your changes, click Create Booking again.'
                                    )
                                )
                            } catch {
                                toast.error(
                                    t(
                                        'Failed to switch offer to Reconfirm and cancel existing booking'
                                    )
                                )
                            }
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
            return
        }

        if (newStatus === OfferStatus.Rejected) {
            await openRejectionFlow()
            return
        }

        try {
            await applyStatus(newStatus)
        } catch {
            toast.error(t('Failed to update status'))
        }
    }

    return (
        <UiInlineSelectPill<OfferStatus>
            ariaLabel={t('Offer Status') as string}
            value={currentStatus as OfferStatus}
            options={options.map((option) => ({
                value: option.value,
                label: option.label,
            }))}
            renderPill={(status) => <UiOfferStatusPill status={status} />}
            onChange={handleChange}
            disabled={currentStatus === OfferStatus.Rejected}
            disabledReason={getOfferEditLockedTooltip(t, OfferStatus.Rejected)}
        />
    )
}

export default UiInlineOfferStatusSelect
